/**
 * Real-Time Updates Chat Context
 *
 * Provides real-time chat updates via Firestore subscriptions.
 * Manages Firebase connection lifecycle for chat-specific updates.
 */

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { ChatRealtimeTokenResponse, RealtimeUpdate } from '~/services/firestore/chatTypes';
import { firestoreService } from '~/services/firestore/firestoreService';

/**
 * Firestore Agent Progress Event structure
 */
export interface FirestoreAgentProgressEvent {
  event_id: string;
  event_type: string;
  step_id?: string | null;
  step_uuid?: string | null;
  title?: string | null;
  message?: string | null;
  metadata?: Record<string, unknown>;
  level?: string | null;
  timestamp: string;
  account_id: string;
  sub_account_id?: string | null;
  claim_number?: string | null;
  task_id?: string | null;
  chat_id?: string | null;
  chat_exchange_id?: string | null;
  scope_type: string;
}

/**
 * Organized updates by exchange ID
 */
export interface OrganizedChatUpdates {
  byExchangeId: Record<string, FirestoreAgentProgressEvent[]>;
  pending: FirestoreAgentProgressEvent[]; // Updates without exchange_id or unknown exchange_id
}

/**
 * Context interface for real-time chat updates
 */
interface IRealTimeUpdatesChatContext {
  updates: RealtimeUpdate[];
  organizedUpdates: OrganizedChatUpdates;
  isConnected: boolean;
  error: string | null;
  connect: (tokenResponse: ChatRealtimeTokenResponse) => Promise<void>;
  disconnect: () => void;
  clearUpdates: () => void;
  registerExchangeIds: (exchangeIds: string[]) => void;
}

const defaultContextValue: IRealTimeUpdatesChatContext = {
  updates: [],
  organizedUpdates: { byExchangeId: {}, pending: [] },
  isConnected: false,
  error: null,
  connect: async () => {
    // No-op: default implementation
  },
  disconnect: () => {
    // No-op: default implementation
  },
  clearUpdates: () => {
    // No-op: default implementation
  },
  registerExchangeIds: () => {
    // No-op: default implementation
  },
};

const RealTimeUpdatesChatContext = createContext<IRealTimeUpdatesChatContext>(defaultContextValue);

interface RealTimeUpdatesChatProviderProps {
  children: ReactNode;
}

/**
 * Real-Time Updates Chat Provider Component
 */
/**
 * Organize updates by exchange ID
 */
const organizeUpdates = (updates: RealtimeUpdate[], knownExchangeIds: Set<string>): OrganizedChatUpdates => {
  const byExchangeId: Record<string, FirestoreAgentProgressEvent[]> = {};
  const pending: FirestoreAgentProgressEvent[] = [];

  updates.forEach((update) => {
    try {
      // The update.data contains the full Firestore document (including metadata like 'type')
      // We need to cast to unknown first, then to the expected type
      const event = update.data as unknown as FirestoreAgentProgressEvent;
      const exchangeId = event.chat_exchange_id;

      if (!exchangeId || !knownExchangeIds.has(exchangeId)) {
        // No exchange ID or unknown exchange ID -> pending
        pending.push(event);
      } else {
        // Known exchange ID -> group by exchange
        if (!byExchangeId[exchangeId]) {
          byExchangeId[exchangeId] = [];
        }
        byExchangeId[exchangeId].push(event);
      }
    } catch (error) {
      console.error('[organizeUpdates] ❌ Error processing update:', error, update);
    }
  });

  return { byExchangeId, pending };
};

export const RealTimeUpdatesChatProvider: React.FC<RealTimeUpdatesChatProviderProps> = ({ children }) => {
  const [updates, setUpdates] = useState<RealtimeUpdate[]>([]);
  const [organizedUpdates, setOrganizedUpdates] = useState<OrganizedChatUpdates>({ byExchangeId: {}, pending: [] });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [knownExchangeIds, setKnownExchangeIds] = useState<Set<string>>(new Set());

  const currentTokenRef = useRef<string | null>(null);

  /**
   * Handle incoming real-time update
   */
  const handleUpdate = useCallback((update: RealtimeUpdate) => {
    try {
      setUpdates((prev) => {
        const newUpdates = [...prev, update];
        console.log('[RealTimeUpdatesChatContext] 📊 Total updates received so far:', newUpdates.length);
        return newUpdates;
      });
    } catch (error) {
      console.error('[RealTimeUpdatesChatContext] ❌ ERROR in handleUpdate:', error);
    }
  }, []);

  /**
   * Re-organize updates whenever updates or known exchange IDs change
   */
  useEffect(() => {
    try {
      const organized = organizeUpdates(updates, knownExchangeIds);
      setOrganizedUpdates(organized);
    } catch (error) {
      console.error('[RealTimeUpdatesChatContext] ❌ ERROR organizing updates:', error);
    }
  }, [updates, knownExchangeIds]);

  /**
   * Reset connection state
   */
  const resetState = useCallback(() => {
    setIsConnected(false);
    setUpdates([]);
    setOrganizedUpdates({ byExchangeId: {}, pending: [] });
    setKnownExchangeIds(new Set());
    setError(null);
    currentTokenRef.current = null;
  }, []);

  /**
   * Clear updates without disconnecting
   */
  const clearUpdates = useCallback(() => {
    console.log('[RealTimeUpdatesChatContext] 🗑️ Clearing updates (keeping connection active)');
    console.log('[RealTimeUpdatesChatContext] Cleared', updates.length, 'updates');
    setUpdates([]);
    setOrganizedUpdates({ byExchangeId: {}, pending: [] });
  }, [updates.length]);

  /**
   * Register exchange IDs as "known" so we can properly organize updates
   */
  const registerExchangeIds = useCallback((exchangeIds: string[]) => {
    setKnownExchangeIds((prev) => {
      const newSet = new Set(prev);
      exchangeIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  /**
   * Disconnect from Firestore and reset state
   */
  const disconnect = useCallback(async () => {
    console.log('[RealTimeUpdatesChatContext] 🔌 Disconnecting from Firestore');
    console.log('[RealTimeUpdatesChatContext] Current state before disconnect:', {
      isConnected,
      updatesCount: updates.length,
      hasError: !!error,
    });
    await firestoreService.disconnect();
    resetState();
    console.log('[RealTimeUpdatesChatContext] ✅ Disconnected and state reset');
  }, [resetState, isConnected, updates.length, error]);

  /**
   * Connect to Firestore for chat real-time updates
   */
  const connect = useCallback(
    async (tokenResponse: ChatRealtimeTokenResponse) => {
      console.log('[RealTimeUpdatesChatContext] 🔄 Connect called');
      console.log('[RealTimeUpdatesChatContext] Current connection state:', { isConnected });

      // If already connected to this token, don't reconnect
      if (isConnected && currentTokenRef.current === tokenResponse.firebase_custom_token) {
        console.log('[RealTimeUpdatesChatContext] ⚠️ Already connected to this chat, skipping reconnection');
        return;
      }

      // If connected to a different token, disconnect first
      if (isConnected) {
        console.log('[RealTimeUpdatesChatContext] 🔌 Disconnecting from previous chat before reconnecting');
        await disconnect();
      }

      setError(null);

      try {
        console.log('[RealTimeUpdatesChatContext] 🚀 Starting Firestore connection process');
        console.log('[RealTimeUpdatesChatContext] 📋 Token info:', {
          expires_at: tokenResponse.expires_at,
          token_length: tokenResponse.firebase_custom_token.length,
          scopes_count: tokenResponse.scopes.length,
        });
        console.log('[RealTimeUpdatesChatContext] 🗄️ Firestore config:', {
          project_id: tokenResponse.firestore_config.project_id,
          host: tokenResponse.firestore_config.host || 'production',
          collection: tokenResponse.firestore_config.collection_name,
        });
        console.log('[RealTimeUpdatesChatContext] 🔍 Query config:', {
          where_clauses_count: tokenResponse.query_config.where_clauses?.length || 0,
          order_by_count: tokenResponse.query_config.order_by?.length || 0,
          where_clauses: tokenResponse.query_config.where_clauses,
          order_by: tokenResponse.query_config.order_by,
        });

        // Initialize Firestore with token
        console.log('[RealTimeUpdatesChatContext] 1️⃣ Initializing Firestore service...');
        await firestoreService.initialize(tokenResponse.firestore_config, tokenResponse.firebase_custom_token);
        console.log('[RealTimeUpdatesChatContext] ✅ Firestore service initialized');

        // Subscribe to updates
        console.log('[RealTimeUpdatesChatContext] 2️⃣ Subscribing to collection updates...');
        const unsubscribe = firestoreService.subscribe(
          tokenResponse.firestore_config.collection_name,
          {
            where_clauses: tokenResponse.query_config.where_clauses || [],
            order_by: tokenResponse.query_config.order_by || [],
          },
          handleUpdate
        );

        if (!unsubscribe) {
          throw new Error('Failed to establish Firestore subscription');
        }
        console.log('[RealTimeUpdatesChatContext] ✅ Subscription established successfully');

        currentTokenRef.current = tokenResponse.firebase_custom_token;
        setIsConnected(true);
        console.log('[RealTimeUpdatesChatContext] 🎉 Connected successfully to chat real-time updates');
        console.log('[RealTimeUpdatesChatContext] 👂 Now listening for Firestore updates...');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to connect to real-time chat updates';
        console.error('[RealTimeUpdatesChatContext] ❌ Connection error:', errorMessage);
        console.error('[RealTimeUpdatesChatContext] Error details:', err);
        setError(errorMessage);
        resetState();
      }
    },
    [isConnected, handleUpdate, disconnect, resetState]
  );

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    console.log('[RealTimeUpdatesChatContext] 🎬 Component mounted and ready');
    return () => {
      console.log('[RealTimeUpdatesChatContext] 🛑 Component unmounting, cleaning up Firestore connection');
      firestoreService.disconnect();
    };
  }, []);

  const contextValue: IRealTimeUpdatesChatContext = {
    updates,
    organizedUpdates,
    isConnected,
    error,
    connect,
    disconnect,
    clearUpdates,
    registerExchangeIds,
  };

  return <RealTimeUpdatesChatContext.Provider value={contextValue}>{children}</RealTimeUpdatesChatContext.Provider>;
};

/**
 * Hook to access real-time updates chat context
 *
 * @throws Error if used outside of RealTimeUpdatesChatProvider
 */
export const useRealTimeUpdatesChat = (): IRealTimeUpdatesChatContext => {
  const context = useContext(RealTimeUpdatesChatContext);
  if (!context) {
    throw new Error('useRealTimeUpdatesChat must be used within RealTimeUpdatesChatProvider');
  }
  return context;
};
